/**
 * GET /api/v1/home/feed → `src/data/home.ts` 목업과 동일 UI 모양
 */

import type {
  HomeCategoryBubbleDto,
  HomeFeedResponseDto,
  HomeHotPostDto,
  HomeRecommendedSpaceDto,
  HomeReviewCardDto,
  HomeVendorCardDto,
} from '../schemas/homeFeed';
import { formatStudioRoomTitle } from '../../lib/spaceDisplay';

/** HOME_HOT_POSTS 행 */
export function homeHotPostCardFromDto(row: HomeHotPostDto) {
  return {
    author: row.author,
    category: row.category,
    comments: row.comments,
    image: hotPostThumbnail(row.thumbnailUrl),
    likes: row.likes,
    title: row.title,
    detailPath: row.detailPath,
  };
}

function hotPostThumbnail(value: string | null) {
  const url = value?.trim() ?? '';
  if (url.startsWith('https://picsum.photos/') || url.startsWith('https://fastly.picsum.photos/')) {
    return '';
  }
  if (!url) {
    return '';
  }
  if (url.startsWith('/')) {
    return url;
  }
  if (!/^https?:\/\//i.test(url)) {
    return '';
  }
  return url;
}

/** HOME_SPACE_CARDS 행 */
export function homeSpaceCardFromDto(row: HomeRecommendedSpaceDto) {
  return {
    detailPath: row.detailPath,
    image: row.imageUrl,
    location: row.location,
    price: row.price,
    rating: row.rating,
    studio: row.studio,
    subtitle: row.subtitle,
    title: formatStudioRoomTitle(row.studio, row.title),
  };
}

/** HOME_REVIEW_CARDS 행 */
export function homeReviewCardFromDto(row: HomeReviewCardDto) {
  return {
    author: row.author,
    date: row.date,
    image: row.imageUrl ?? '',
    rating: row.rating,
    spaceName: row.spaceTitle,
    text: row.text,
  };
}

/** HOME_CATEGORY_BUBBLES 행 — 검색 쿼리는 별도로 라우팅 합의 */
export function homeCategoryBubbleFromDto(row: HomeCategoryBubbleDto) {
  return {
    accent: row.accentHex,
    label: row.label,
    searchQueryOrTag: row.searchQueryOrTag,
  };
}

export function formatVendorRoomCount(value: number | string | null | undefined) {
  if (typeof value === 'number') {
    return value > 0 ? `${value}개 공간` : '공간 정보';
  }

  const text = String(value ?? '').trim();
  if (!text) return '공간 정보';
  if (/^\d+$/.test(text)) return `${Number(text)}개 공간`;
  return text;
}

/** HOME_VENDOR_CARDS 행 */
export function homeVendorCardFromDto(row: HomeVendorCardDto) {
  return {
    slug: row.slug,
    name: row.name,
    description: row.description,
    location: row.location,
    roomCount: formatVendorRoomCount(row.roomCount),
    rating: row.rating,
    imageUrl: row.imageUrl,
    detailPath: row.detailPath,
  };
}

export function normalizeHomeFeedForUi(dto: HomeFeedResponseDto) {
  return {
    hotPosts: (dto.hotPosts ?? []).map(homeHotPostCardFromDto),
    recommendedSpaces: (dto.recommendedSpaces ?? []).map(homeSpaceCardFromDto),
    reviewCards: (dto.reviewCards ?? []).map(homeReviewCardFromDto),
    categoryBubbles: (dto.categoryBubbles ?? []).map(homeCategoryBubbleFromDto),
    vendorCards: (dto.vendorCards ?? []).map(homeVendorCardFromDto),
  };
}
